import { useState } from "react";
import { apiRequest } from "@vojomo-core/shared";
import { Button, Form, Input, Radio, Alert } from "antd";
import BaseLayout from "../layouts/BaseLayout";

type Target = "all" | "company" | "user";

export function Notifications(): React.ReactElement {
  const [form] = Form.useForm();
  const [target, setTarget] = useState<Target>("all");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [result, setResult] = useState<{
    success: boolean;
    message: string;
  } | null>(null);

  const handleSubmit = async (values: {
    title: string;
    message: string;
    target: Target;
    target_value?: string;
  }) => {
    setIsSubmitting(true);
    setResult(null);
    try {
      const data = await apiRequest<{ success: boolean; message: string }>({
        subRoute: "admin",
        path: "/notification/broadcast",
        method: "POST",
        body: {
          title: values.title,
          message: values.message,
          target: values.target,
          target_value: values.target_value ?? "",
        },
      });
      setResult(data);
      if (data.success) {
        form.resetFields();
        setTarget("all");
      }
    } catch {
      setResult({ success: false, message: "An unexpected error occurred." });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <BaseLayout>
      <div className="max-w-lg">
        <h3 className="mb-4">Broadcast Notification</h3>
        <p className="text-gray-500 mb-6 text-sm">
          Send an in-app notification to all users, a specific company, or a
          specific user.
        </p>

        {result && (
          <Alert
            className="mb-4"
            type={result.success ? "success" : "error"}
            message={result.message}
            showIcon
            closable
            onClose={() => setResult(null)}
          />
        )}

        <Form
          form={form}
          layout="vertical"
          initialValues={{ target: "all" }}
          onFinish={handleSubmit}
        >
          <Form.Item
            label="Title"
            name="title"
            rules={[{ required: true, message: "Title is required" }]}
          >
            <Input placeholder="e.g. New feature available!" maxLength={100} />
          </Form.Item>

          <Form.Item
            label="Message"
            name="message"
            rules={[{ required: true, message: "Message is required" }]}
          >
            <Input.TextArea
              placeholder="Notification body text..."
              rows={4}
              maxLength={500}
              showCount
            />
          </Form.Item>

          <Form.Item label="Send to" name="target">
            <Radio.Group
              onChange={(e) => setTarget(e.target.value)}
              value={target}
            >
              <Radio value="all">All users</Radio>
              <Radio value="company">Specific company</Radio>
              <Radio value="user">Specific user</Radio>
            </Radio.Group>
          </Form.Item>

          {target === "company" && (
            <Form.Item
              label="Company name"
              name="target_value"
              rules={[{ required: true, message: "Company name is required" }]}
            >
              <Input placeholder="e.g. Acme Corp" />
            </Form.Item>
          )}

          {target === "user" && (
            <Form.Item
              label="User email"
              name="target_value"
              rules={[
                { required: true, message: "User email is required" },
                { type: "email", message: "Enter a valid email address" },
              ]}
            >
              <Input placeholder="user@example.com" />
            </Form.Item>
          )}

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              loading={isSubmitting}
              style={{ backgroundColor: "rgb(44, 21, 97)" }}
            >
              Send notification
            </Button>
          </Form.Item>
        </Form>
      </div>
    </BaseLayout>
  );
}
